/**
 * Main's CreateSubAccount on the port (Trello xltSi5PP, FEM-13).
 *
 * Main created a person in two stores: `register()` wrote pacmny-be's
 * PostgreSQL user, then CreateSubAccount wrote keyon's `account_user` with the
 * groups, the credential files and the queued patients. The port has one
 * store, and `register()` (userApi.js) is already `POST /users` — with the
 * groups, the credential uids and the queued patients in that one request, so
 * the person is created whole or not at all. So a CreateSubAccount that
 * names the person `register()` just created (`id`, which both Add User and
 * Users and Status set from its answer) does NOT create them again: it only
 * attaches a licence or insurance file the form picked but could not upload
 * ahead of the save. Before this, the second create failed on the address
 * the first had just taken, and the form reported a failed create for a
 * person who existed.
 *
 * A CreateSubAccount that names nobody is a create in its own right, mapped
 * from main's form fields. Its groups are EVERY group the form chose, sent as
 * `group_ids` from `additional_group_ids` (a JSON string), so the API's
 * member and confer rules see each one. `primary_group_name` is deliberately
 * not mapped: the API joins the account type's own group itself, and a group
 * named any other way would be one those rules never saw.
 */

import api, { ApiError } from './client';
import { routeId, okMessage, okMsg, dateOnly } from './bridgeShapes';
import { isFileUid } from '@/utils/fileUrl';
import { legacyAccountType } from '@/utils/accountTypeLabel';

/** Main's account-type words -> the API's role. */
const ROLE_BY_ACCOUNT_TYPE = {
  'Administrator': 'administrator',
  'Provider': 'provider',
  'Registered Nurse': 'registered_nurse',
  'Licensed Practical Nurse': 'licensed_practical_nurse',
  'Home Health Aid': 'home_health_aid',
  'Home Health Aide': 'home_health_aid',
  'Allied Professional': 'allied_professional',
  'Geriatric Care Manager': 'geriatric_care_manager',
  'Patient': 'patient',
};

/** `@/data/genders` is `{Male: 1, Female: 2}`; the API's enum is lower case. */
const GENDER_BY_LEGACY_ID = { 1: 'male', 2: 'female' };

const blank = (v) => v === null || v === undefined || String(v).trim() === '';
const text = (v) => (blank(v) ? undefined : String(v).trim());

/** A JSON-string list of ids (main's multipart form parts), or the list itself. */
export function idList(raw) {
  let list = raw;

  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch (e) {
      return undefined;
    }
  }

  if (!Array.isArray(list)) return undefined;

  const ids = list.map(routeId).filter((v) => v !== null);

  return ids.length ? ids : undefined;
}

function roleOf(p) {
  const label = String(p.accountType || p.accountrole || '').trim();

  return ROLE_BY_ACCOUNT_TYPE[label] || label.toLowerCase().replace(/\s+/g, '_');
}

/**
 * Main's CreateSubAccount fields -> POST /users, for a create that
 * `register()` has not already made.
 */
export function createBody(p) {
  return {
    role: roleOf(p),
    first_name: text(p.Firstname ?? p.first ?? p.firstname),
    last_name: text(p.Lastname ?? p.last ?? p.lastname),
    email: text(p.email),
    username: text(p.username),
    phone_primary: text(p.cell ?? p.phone),
    address_line1: text(p.Address ?? p.address),
    city: text(p.prCity ?? p.city),
    state: text(p.prState ?? p.state),
    postal_code: text(p.prZip ?? p.zip),
    dob: blank(p.dob) ? undefined : dateOnly(p.dob),
    gender: GENDER_BY_LEGACY_ID[Number(p.gender)] || (['male', 'female', 'other', 'unknown'].includes(p.gender) ? p.gender : undefined),
    npi: text(p.npi),
    professional_license: text(p.licenseNum),
    license_state: text(p.licenseState ?? p.practicestate),
    // An Allied Professional's specialty (AddUserForm's option value, e.g.
    // `BEHAVIORAL_HEALTH`), which the API classifies as main (PR #154) does.
    specialty: text(p.specialty),
    provider_type: text(p.providerType),
    certificate_number: text(p.aidCerticateValue ?? p.certificate),
    // Every group the form chose, so the API's member and confer rules see
    // each; never primary_group_name (see the module docblock).
    group_ids: idList(p.additional_group_ids),
    pending_patient_ids: idList(p.pending_patient_unique_IDs),
    license_file_uid: isFileUid(p.license_file_uid) ? p.license_file_uid : undefined,
    insurance_file_uid: isFileUid(p.insurance_file_uid) ? p.insurance_file_uid : undefined,
  };
}

/** A picked file the form could not upload ahead of the save, to its slot. */
async function attachPicked(userId, slot, file) {
  const form = new FormData();
  form.append('type', slot);
  form.append('file', file);
  form.append('is_current', '1');

  await api.upload(`/users/${userId}/documents`, form);
}

function isFile(v) {
  return typeof File !== 'undefined' && v instanceof File;
}

/** The KEYON_FLAT_OPERATIONS entry's `local`. */
export async function createSubAccount(p) {
  const existing = routeId(p.id);

  try {
    if (existing !== null) {
      // register() made the person, with their groups, uids and queued
      // patients. Only a file that never got a uid is left.
      if (isFile(p.licenseFile)) await attachPicked(existing, 'license', p.licenseFile);
      if (isFile(p.insuranceFile)) await attachPicked(existing, 'insurance', p.insuranceFile);

      return okMessage('Account Created Successfully', { status: 'Successfull', userId: existing });
    }

    const body = createBody(p);
    const files = { license_file: isFile(p.licenseFile) ? p.licenseFile : undefined, insurance_file: isFile(p.insuranceFile) ? p.insuranceFile : undefined };
    let user;

    if (files.license_file || files.insurance_file) {
      const form = new FormData();
      form.append('user', JSON.stringify(body));
      if (files.license_file && !body.license_file_uid) form.append('license_file', files.license_file);
      if (files.insurance_file && !body.insurance_file_uid) form.append('insurance_file', files.insurance_file);
      user = await api.upload('/users', form);
    } else {
      user = await api.post('/users', body);
    }

    return okMessage('Account Created Successfully', { status: 'Successfull', userId: user && user.id });
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;

    // Main's refusal words for the duplicates its form surfaces on a field.
    if (error.code === 'npi_already_registered') {
      return { message: 'NPI Already Registered', error: error.message, code: error.code };
    }

    if (error.code === 'licence_already_registered') {
      return { message: 'License Already Registered', error: error.message, code: error.code };
    }

    // Main's CreateSubAccount guard for a Home Health Aid (GetIndividualAid).
    if (error.code === 'certificate_already_registered') {
      return { message: 'Certificate Already Registered', error: error.message, code: error.code };
    }

    return { message: error.message, error: error.message, code: error.code, status: 'Error' };
  }
}

// --- Edit User's record (AccountUser.SearchAccountForEdit) ------------------

/** `communication_preference` in main's words ('Email'), which Edit User binds. */
const PREFERENCE_LABELS = { email: 'Email', sms: 'SMS', phone: 'Phone', mail: 'Mail', all: 'All' };

/** Which of main's role tables a person's account type lived in. */
function roleTable(role) {
  if (role === 'patient') return 'patients';
  if (role === 'provider' || role === 'administrator') return 'providers';

  return 'nurses';
}

function licence(user, type) {
  return (Array.isArray(user.licenses) ? user.licenses : []).find((l) => l.type === type) || null;
}

function slotUid(user, slot) {
  const doc = (Array.isArray(user.documents) ? user.documents : []).find((d) => (d.slot || d.category) === slot);

  return doc && isFileUid(doc.file_uid) ? doc.file_uid : null;
}

/**
 * One person as main's SearchAccountForEdit merged them (keyon
 * processClass::SearchAccountForEdit): the identity, the account type in
 * main's words, which role table they were found in, and the row each table
 * held — which is what Edit User prefills from.
 *
 * Every person is found in their role's table: in the port the account type
 * is the person (PATCH /users refuses a change of it), so Edit User always
 * edits in place and never takes main's "add a new account type" path, whose
 * second create would only be refused.
 */
export function editRecord(user) {
  const role = user.role || user.account_type || '';
  const table = roleTable(role);
  const npi = licence(user, 'npi');
  const state = licence(user, 'state_professional');
  const certificate = licence(user, 'hha_certificate');
  const professional = (npi && npi.number) || (state && state.number) || '';

  const accountUser = {
    unique_ID: String(user.id),
    user_Fname: user.first_name || '',
    user_Lname: user.last_name || '',
    user_email: user.email || '',
    username: user.username || '',
    account_type: legacyAccountType(role),
    user_phone: user.phone_primary || '',
    address: user.address_line1 || '',
    city: user.city || '',
    state: user.state || '',
    zip: user.postal_code || '',
    communication_preference: PREFERENCE_LABELS[user.communication_preference] || 'Email',
    proffesionalLicense: professional,
    // The internal EMR licence is the API's own and is not an edit.
    emr_licensenumber: '',
    insuranceCompany: user.malpractice_carrier || '',
    insuranceStart: user.malpractice_starts_on || '',
    insuranceEnd: user.malpractice_ends_on || '',
    perIncLimit: user.malpractice_per_incident_limit ?? '',
    annLimit: user.malpractice_aggregate_limit ?? '',
  };

  return {
    uniqueID: String(user.id),
    accountnumber: user.organization_number != null ? String(user.organization_number) : '',
    firstname: user.first_name || '',
    lastname: user.last_name || '',
    email: user.email || '',
    username: user.username || '',
    accountType: legacyAccountType(role),
    hasProfessionalLicense: professional !== '',
    foundIn: ['account_user', table],
    account_user: accountUser,
    accounts: null,
    providers: table === 'providers' ? {
      // The person themself: Edit User's providers branch posts
      // Provider.UpdateProvider with this id and main's account-level
      // sentinel patient '0099', which `updateProvider` sends to the person
      // (PATCH /users) rather than to a chart's provider list.
      providerid: String(user.id),
      npinumber: (npi && npi.number) || '',
      providerType: user.provider_segment || '',
      addr1: user.address_line1 || '',
      city: user.city || '',
      state: user.state || '',
      postalcode: user.postal_code || '',
      tel: user.phone_primary || '',
    } : null,
    nurses: table === 'nurses' ? {
      // Main's `nurses.caregivetype`: Edit User reads it to tell an aide
      // (`isHHA`) from a nurse, and sends an aide's number as her certificate
      // ('Update HHA') rather than a state licence.
      caregivetype: legacyAccountType(role),
      licensetype: (state && state.profession) || '',
      licensenum: (state && state.number) || '',
      certificatenumber: (certificate && certificate.number) || '',
      // The address's state. Main's one `nurses.state` column served as both
      // the address and the licence state, and Edit User copies it into
      // both; in the port they are two facts (users.state, the licence's
      // own state), so the licence state travels under its own key and a
      // save no longer writes the licence state over the address.
      state: user.state || '',
      licenseState: (state && state.state) || '',
      addr1: user.address_line1 || '',
      city: user.city || '',
      zip: user.postal_code || '',
      primaryphone: user.phone_primary || '',
      dob: user.date_of_birth || '',
      insuranceCompany: user.malpractice_carrier || '',
      insuranceStart: user.malpractice_starts_on || '',
      insuranceEnd: user.malpractice_ends_on || '',
      perIncidentAmnt: user.malpractice_per_incident_limit ?? '',
      annualAggAmnt: user.malpractice_aggregate_limit ?? '',
      license_file_uid: slotUid(user, 'license'),
      insurance_file_uid: slotUid(user, 'insurance'),
    } : null,
    patients: table === 'patients' ? {
      // The chart's id (GET /users?include=patient), which Edit User sends
      // as `patientid` to save the chart's own fields: the person's id is
      // not the chart's, and with none the save addressed no chart and
      // failed after the profile had already been written.
      mid: user.patient_id != null ? String(user.patient_id) : '',
      date_of_birth: user.date_of_birth || '',
      gender: user.gender || '',
      address: user.address_line1 || '',
      city: user.city || '',
      state: user.state || '',
      zip: user.postal_code || '',
      contact_number: user.phone_primary || '',
    } : null,
  };
}

// --- Provider.UpdateProvider -------------------------------------------------

/**
 * Main's patient id for "no patient": the account-level provider record that
 * Edit User's providers branch writes (`patientid: p.patientid || "0099"`).
 */
const ACCOUNT_LEVEL_PATIENT = '0099';

function isAccountLevel(patientid) {
  const patient = String(patientid ?? '').trim();

  return patient === '' || patient === ACCOUNT_LEVEL_PATIENT;
}

/**
 * Main's UpdateProvider wrote one `providers` row. Two kinds reach it:
 *
 *  - a chart's provider (the provider cards: CurrentProviders, MyAccount,
 *    PatientCards), which is `PATCH /patients/{patient}/providers/{id}`;
 *  - the account-level record of a Provider or Administrator, which Edit User
 *    saves after UpdateAccountUser with main's sentinel patient '0099'. In
 *    the port that record IS the person, and UpdateAccountUser has already
 *    written their name, address and phone; what is left is the provider
 *    type and the NPI, which PATCH /users takes. The NPI is main's
 *    `providers.npinumber`, written in this step: UpdateAccountUser sends
 *    Edit User's other licence field, which for these roles still holds the
 *    NPI the form was opened with, so an edited NPI arrives only here. Sent
 *    to a chart, it named patient 99 and provider `undefined`, answered 404,
 *    and every Edit User save of a Provider or Administrator ended in an
 *    error after the profile had saved.
 *
 * Neither carries the e-mail: on the person it is a sign-in identifier (the
 * credentials route), and on a chart's provider the card sends what it read.
 */
export async function updateProvider(p) {
  if (isAccountLevel(p.patientid)) {
    const person = routeId(p.providerid);

    if (person === null) {
      throw new ApiError({
        status: 0,
        code: 'bridge_unresolved_target',
        message: 'This provider could not be updated: no person was identified.',
      });
    }

    const providerType = text(p.providerType);
    const npi = text(p.npinumber);
    const body = {
      ...(providerType !== undefined ? { provider_type: providerType } : {}),
      // Only a real NPI: empty is no change, never a clear, as for every
      // other licence field the form sends whether or not it was touched.
      ...(npi !== undefined && /^\d{10}$/.test(npi) ? { npi } : {}),
    };

    if (Object.keys(body).length) {
      await api.patch(`/users/${person}`, body);
    }

    return okMsg('Updated');
  }

  await api.patch(`/patients/${routeId(p.patientid)}/providers/${routeId(p.providerid)}`, {
    first_name: p.firstname,
    last_name: p.lastname,
    email: p.email,
    phone: p.phone,
    cell: p.cell,
    fax: p.fax,
    address_line1: p.address1,
    address_line2: p.address2,
    city: p.city,
    state: p.state,
    postal_code: p.zip,
    npi: p.npinumber,
    status: p.status,
    provider_type: p.providerType,
  });

  return okMsg('Updated');
}

// --- ManageNurseList.InternalUpdateNurse -----------------------------------

/**
 * Main spells the aide's account type both ways, and the port's rows carry
 * the API's `home_health_aid`.
 */
export function isAideAccountType(type) {
  const t = String(type || '').trim().toLowerCase().replace(/_/g, ' ');

  return t === 'home health aid' || t === 'home health aide';
}

/**
 * A date field of the save: absent is no change (the key is left out), an
 * empty one is a clear, as the card asked. `dateOnly(undefined)` is null, and
 * sent as null it cleared the column on every save from a caller that never
 * had the field - Edit User sends the date of birth as `bdayear`, so each of
 * its nurse saves wiped the person's date of birth.
 */
function dateField(v) {
  return v === undefined ? undefined : dateOnly(v);
}

/**
 * Main's slot keys: a uid points the slot at that file, null empties it, an
 * absent key leaves it - so a key is forwarded only when the call site sent
 * it (credentialUploadForm.changedUidKeys).
 */
function credentialSlotKeys(p) {
  const keys = {};

  ['license_file_uid', 'insurance_file_uid'].forEach((key) => {
    if (p && Object.prototype.hasOwnProperty.call(p, key)) keys[key] = p[key] === '' ? null : p[key];
  });

  return keys;
}

/** The PATCH /users body of a nurse or aide card save. */
export function nurseUpdateBody(p, aide) {
  return {
    // Neither the account type nor the e-mail: PATCH /users refuses both.
    // An account type is not an edit, and the e-mail is the sign-in
    // identifier, which changes only through the credentials route (Trello
    // bMZnxWqc). Main's card wrote the `nurses` row's own contact copy.
    first_name: p.firstname,
    last_name: p.lastname,
    // The Bio cards spell it `primearyphone`, as main's InternalAddNurse did.
    phone_primary: p.primephone ?? p.primearyphone,
    phone_secondary: p.secondaryphone,
    address_line1: p.address1,
    address_line2: p.address2,
    city: p.city,
    state: p.state,
    postal_code: p.zip,
    dob: dateField(p.dob !== undefined ? p.dob : p.bdayear),
    // No `status` from `p.activestatus`: on main that field is the Nursys
    // Active flag (YES/NO, written back by the roster check), and the aide
    // store sends a literal "Active". Neither is an instruction to change
    // whether the person may sign in.
    //
    // An aide's number is her HHA certificate, not a state licence. Sent as
    // `professional_license` it became a state_professional row carrying the
    // certificate number.
    ...(aide
      ? { certificate_number: p.licensnum }
      : { professional_license: p.licensnum, license_type: p.licenseType, license_state: p.jurisdiction }),
    license_expires_on: dateField(p.licenseExpDt),
    insurance_company: p.insuranceCompany,
    insurance_start_date: dateField(p.insuranceStartDt),
    insurance_end_date: dateField(p.insuranceEndDate),
    insurance_per_incident_amount: p.perIncdedentAmount,
    insurance_annual_aggregate_amount: p.annualAggAmount,
    practice_setting: p.hospitalpracsetting,
    practice_setting_other: p.hostpracsettingother,
    ...credentialSlotKeys(p),
  };
}

/**
 * Main's InternalUpdateNurse: a nurse or aide card's save, as PATCH /users.
 *
 * Whether the number on the card is a state licence or an aide's HHA
 * certificate is the person's account type. Edit User says so ('Update HHA',
 * main's `nurses.caregivetype`) and the card stores send `nurseType`; the Bio
 * cards (CurrentNurseBio - which the aide roster renders too - AccountNurseBio,
 * GridNurseBio, PatientNurseBio) say nothing, so the person is asked. Before,
 * an aide's certificate typed on one of those went as a state licence.
 */
export async function internalUpdateNurse(p) {
  const person = routeId(p.providerid ?? p.unique_ID);

  if (person === null) {
    throw new ApiError({
      status: 0,
      code: 'bridge_unresolved_target',
      message: 'This nurse could not be updated: no person was identified.',
    });
  }

  let aide = p.action === 'Update HHA' || isAideAccountType(p.nurseType);

  if (!aide && blank(p.action) && blank(p.nurseType) && !blank(p.licensnum)) {
    const user = await api.get(`/users/${person}`);

    aide = isAideAccountType(user && (user.role || user.account_type));
  }

  await api.patch(`/users/${person}`, nurseUpdateBody(p, aide));

  return okMessage('Updated');
}

/**
 * Main's search term, as the API's `?q=`. The Administration chooser sends
 * `%` for "everyone" (keyon answered nothing for a blank term); the API
 * searches the text it is given, so main's wildcard is no term at all.
 */
export function searchTerm(raw) {
  const term = String(raw ?? '').trim();

  return term === '' || term === '%' ? undefined : term;
}

export default { createSubAccount, createBody, idList, editRecord, searchTerm, updateProvider, internalUpdateNurse, nurseUpdateBody, isAideAccountType };
